import { createContext, useEffect, useRef, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
//routes
import { allRoutes, businessRoute, playgroundRoute, DefaultRoute } from "./routingConstants/RoutesConfig";
//root component
import App from "../App";
//pages
import NotFoundPage from "../pages/not-found/not-found";

export const LocaleContext = createContext({
    locale: "",
    setLocale: newLocale => {},
});

export const ItineraryContext = createContext({
    routes: [],
    // eslint-disable-next-line
    setRoutes: newRoute => {},
});

const AVAILABLE_LOCALES = ["fa", "en", "ar"];
const DEFAULT_LOCALE = "fa";
const LOADING_TIMEOUT = 300;

const LangRouter = () => {
    //general variables
    const { i18n } = useTranslation();
    const { pathname, search, hash } = useLocation();
    const navigate = useNavigate();
    const pathnameLocale = pathname.substring(1, 3).toLowerCase();
    const [locale, setLocale] = useState(DEFAULT_LOCALE);
    const [_isLoading, setIsLoading] = useState(true);
    const loaderTimerRef = useRef();

    // Set body direction based on language
    useEffect(() => {
        document.body.dir = i18n.dir(i18n.language);
    }, [i18n.language]);

    // Handle initial loading state
    useEffect(() => {
        loaderTimerRef.current = setTimeout(() => {
            setIsLoading(false);
        }, LOADING_TIMEOUT);

        return () => clearTimeout(loaderTimerRef.current);
    }, []);

    // Handle pathname changes
    useEffect(() => {
        if (AVAILABLE_LOCALES.includes(pathnameLocale)) {
            updateLocale(pathnameLocale);
        } else if (pathname === "/") {
            updateLocale(DEFAULT_LOCALE);
        }
    }, [pathname, pathnameLocale]);

    // Handle locale changes
    useEffect(() => {
        const lang = AVAILABLE_LOCALES.includes(pathnameLocale) ? pathnameLocale : DEFAULT_LOCALE;
        updateLanguage(lang);
    }, [locale, pathnameLocale]);

    const updateLanguage = lang => {
        document.documentElement.setAttribute("lang", lang);
        i18n.changeLanguage(lang);
    };

    const updateLocale = newLocale => {
        const newPath = `/${newLocale}${pathname.substring(3)}`;
        const isRootPath = newPath === `/${newLocale}/` || newPath === `/${newLocale}` || pathname === "/";

        if (locale !== newLocale) {
            if (isRootPath) {
                navigate(`/${newLocale}/flight-search/domestic`);
            } else {
                navigate(`${newPath}${hash}${search}`);
            }
            setLocale(newLocale);
        } else if (isRootPath) {
            navigate(`/${newLocale}/flight-search/domestic`);
        }
    };

    const renderRoutes = (routes, parentLocale) => {
        return routes.map((route, index) => (
            <Route key={index} path={route.path(parentLocale)} element={route.element}>
                {route.children && renderRoutes(route.children, parentLocale)}
            </Route>
        ));
    };

    return (
        <LocaleContext.Provider value={{ locale, setLocale: updateLocale }}>
            <Routes>
                <Route path={`/${locale}`} element={<App />}>
                    {renderRoutes(allRoutes, locale)}
                </Route>
                <Route path={DefaultRoute.path()} element={DefaultRoute.element} />
                <Route path={businessRoute.path()} element={businessRoute.element} />
                <Route path={playgroundRoute.path()} element={playgroundRoute.element} />
                <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </LocaleContext.Provider>
    );
};

export default LangRouter;
